const React = require("react");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const h = React.createElement;

// Barre de filtres composite au-dessus de la grille produits. Composant
// contrôlé : aucun state de filtre n'est possédé ici (voir `value`/
// `onChange`, possédés par ProductCollectionPage.js, qui décide seul du
// debounce -- voir le commentaire sur `onChange` plus bas). Ce fichier ne
// gère QUE ses deux propres données de référence (liste des fournisseurs
// pour le select, arborescence catégories pour le picker), qui ne dépendent
// pas de l'état des filtres/pagination des produits.
//
// Toutes les valeurs proposées ici sont adossées à un vrai champ persisté
// (voir le tableau de correspondance dans le plan) -- volontairement AUCUN
// filtre "Warehouse" et AUCUNE option de tri "Best Seller"/"Most Orders"/
// "Best Rating" : ni l'un ni l'autre n'a de donnée réelle sur
// DropshippingProduct aujourd'hui.
const STATUS_OPTIONS = [
  { value: "", labelKey: "h_cj_filter_status_all", fallback: "All statuses" },
  { value: "in_stock", labelKey: "h_cj_filter_status_in_stock", fallback: "In Stock" },
  { value: "out_of_stock", labelKey: "h_cj_filter_status_out_of_stock", fallback: "Out of Stock" },
  { value: "available", labelKey: "h_cj_filter_status_available", fallback: "Available" },
  { value: "unavailable", labelKey: "h_cj_filter_status_unavailable", fallback: "Unavailable" },
];

const SORT_OPTIONS = [
  { value: "newest", labelKey: "h_cj_sort_newest", fallback: "Newest" },
  { value: "oldest", labelKey: "h_cj_sort_oldest", fallback: "Oldest" },
  { value: "price_asc", labelKey: "h_cj_sort_price_asc", fallback: "Price: Low to High" },
  { value: "price_desc", labelKey: "h_cj_sort_price_desc", fallback: "Price: High to Low" },
];

const FilterBar = ({ value, onChange, onReset }) => {
  const { api, t } = useCJDropshipping();
  const [supplierMenuOpen, setSupplierMenuOpen] = React.useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = React.useState(false);
  const [openFirst, setOpenFirst] = React.useState(null);
  const [openSecond, setOpenSecond] = React.useState(null);

  // Données de référence propres à cette barre -- capées à 100 fournisseurs
  // (assez pour un menu déroulant, pas une pagination à part entière).
  const suppliersResource = useDropshippingResource(() => api.listSuppliers({ limit: 100 }), [api]);
  const suppliers = suppliersResource.data?.suppliers || [];
  const categoriesResource = useDropshippingResource(() => api.getCategoriesNormalized(), [api]);
  const categories = categoriesResource.data?.categories || [];

  const selectedSupplier = suppliers.find((supplier) => supplier._id === value.supplierId);
  const countryOptions = React.useMemo(() => {
    const set = new Set();
    suppliers.forEach((supplier) => (supplier.shippingCountries || []).forEach((code) => set.add(code)));
    return [...set].sort();
  }, [suppliers]);

  const priceInvalid = value.minPrice !== "" && value.maxPrice !== "" && Number(value.minPrice) > Number(value.maxPrice);
  const deliveryInvalid = value.minDeliveryDays !== "" && value.maxDeliveryDays !== "" && Number(value.minDeliveryDays) > Number(value.maxDeliveryDays);

  return h("div", { className: "cjds-filterbar" },
    h("div", { className: "cjds-filterbar-group" },
      h("input", {
        className: "cjds-input",
        value: value.keyword,
        onChange: (event) => onChange({ keyword: event.target.value }),
        placeholder: t("h_cj_search_placeholder", "Search by product name, SKU or product ID"),
        "aria-label": t("h_cj_search", "Search products"),
      })
    ),

    h("div", { className: "cjds-filterbar-group" },
      h("button", {
        type: "button",
        className: "cjds-btn secondary",
        onClick: () => setSupplierMenuOpen((open) => !open),
      }, selectedSupplier ? selectedSupplier.name : t("h_cj_filter_supplier", "Supplier")),
      supplierMenuOpen ? h("div", { className: "cjds-supplier-filter-menu" },
        h("button", {
          type: "button",
          className: "cjds-supplier-filter-row",
          onClick: () => { onChange({ supplierId: "" }); setSupplierMenuOpen(false); },
        }, t("h_cj_filter_supplier_all", "All suppliers")),
        ...suppliers.map((supplier) => h("button", {
          key: supplier._id,
          type: "button",
          className: "cjds-supplier-filter-row",
          onClick: () => { onChange({ supplierId: supplier._id }); setSupplierMenuOpen(false); },
        },
          supplier.logo ? h("img", { className: "cjds-supplier-filter-logo", src: supplier.logo, alt: "" }) : null,
          h("span", null, `${supplier.name}${supplier.country ? ` (${supplier.country})` : ""} — ${supplier.totalProducts || supplier.productCount || 0} ${t("h_cj_products_count", "Products")}`)
        ))
      ) : null
    ),

    h("div", { className: "cjds-filterbar-group" },
      h("button", {
        type: "button",
        className: "cjds-btn secondary",
        onClick: () => setCategoryMenuOpen((open) => !open),
      }, value.categoryId ? t("h_cj_filter_category_selected", "Category selected") : t("h_cj_filter_category", "Category")),
      categoryMenuOpen ? h("div", { className: "cjds-category-picker-panel" },
        h("button", {
          type: "button",
          className: "cjds-category-leaf",
          onClick: () => { onChange({ categoryId: "" }); setCategoryMenuOpen(false); },
        }, t("h_cj_filter_category_all", "All categories")),
        ...categories.map((level1) => h("div", { key: level1.id, className: "cjds-category-group" },
          h("button", {
            type: "button",
            className: "cjds-category-toggle",
            onClick: () => setOpenFirst((current) => current === level1.id ? null : level1.id),
          }, level1.name),
          openFirst === level1.id ? h("div", { className: "cjds-category-children" },
            ...(level1.children || []).map((level2) => h("div", { key: level2.id, className: "cjds-category-group" },
              h("button", {
                type: "button",
                className: "cjds-category-toggle cjds-category-toggle--secondary",
                onClick: () => setOpenSecond((current) => current === level2.id ? null : level2.id),
              }, level2.name),
              openSecond === level2.id ? h("div", { className: "cjds-category-children" },
                ...(level2.children || []).map((leaf) => h("button", {
                  key: leaf.id,
                  type: "button",
                  className: "cjds-category-leaf",
                  onClick: () => { onChange({ categoryId: leaf.id }); setCategoryMenuOpen(false); },
                }, leaf.name))
              ) : null
            ))
          ) : null
        ))
      ) : null
    ),

    h("div", { className: "cjds-filterbar-group" },
      h("select", {
        className: "cjds-input",
        value: value.countryCode,
        onChange: (event) => onChange({ countryCode: event.target.value }),
        "aria-label": t("h_cj_filter_country", "Shipping country"),
      },
        h("option", { value: "" }, t("h_cj_filter_country_all", "All shipping countries")),
        ...countryOptions.map((code) => h("option", { key: code, value: code }, code))
      )
    ),

    h("div", { className: "cjds-filterbar-group" },
      h("select", {
        className: "cjds-input",
        value: value.status,
        onChange: (event) => onChange({ status: event.target.value }),
        "aria-label": t("h_cj_filter_status", "Product status"),
      }, ...STATUS_OPTIONS.map((option) => h("option", { key: option.value, value: option.value }, t(option.labelKey, option.fallback))))
    ),

    h("div", { className: "cjds-filterbar-group" },
      h("span", { className: "cjds-muted" }, t("h_cj_filter_price", "Price")),
      h("div", { className: `cjds-filterbar-range ${priceInvalid ? "cjds-filterbar-invalid" : ""}`.trim() },
        h("input", {
          type: "number", min: "0", step: "0.01", className: "cjds-input",
          value: value.minPrice, placeholder: t("h_cj_filter_price_min", "Min"),
          onChange: (event) => onChange({ minPrice: event.target.value }),
        }),
        h("input", {
          type: "number", min: "0", step: "0.01", className: "cjds-input",
          value: value.maxPrice, placeholder: t("h_cj_filter_price_max", "Max"),
          onChange: (event) => onChange({ maxPrice: event.target.value }),
        })
      ),
      priceInvalid ? h("p", { className: "cjds-alert" }, t("h_cj_filter_price_invalid", "Minimum price must not exceed maximum price.")) : null
    ),

    h("div", { className: "cjds-filterbar-group" },
      h("span", { className: "cjds-muted" }, t("h_cj_filter_delivery", "Delivery Days")),
      h("div", { className: `cjds-filterbar-range ${deliveryInvalid ? "cjds-filterbar-invalid" : ""}`.trim() },
        h("input", {
          type: "number", min: "0", step: "1", className: "cjds-input",
          value: value.minDeliveryDays, placeholder: t("h_cj_filter_delivery_min", "Min"),
          onChange: (event) => onChange({ minDeliveryDays: event.target.value }),
        }),
        h("input", {
          type: "number", min: "0", step: "1", className: "cjds-input",
          value: value.maxDeliveryDays, placeholder: t("h_cj_filter_delivery_max", "Max"),
          onChange: (event) => onChange({ maxDeliveryDays: event.target.value }),
        })
      ),
      deliveryInvalid ? h("p", { className: "cjds-alert" }, t("h_cj_filter_delivery_invalid", "Minimum delivery days must not exceed maximum.")) : null
    ),

    h("div", { className: "cjds-filterbar-group" },
      h("select", {
        className: "cjds-input",
        value: value.sort,
        onChange: (event) => onChange({ sort: event.target.value }),
        "aria-label": t("h_cj_filter_sort", "Sort by"),
      }, ...SORT_OPTIONS.map((option) => h("option", { key: option.value, value: option.value }, t(option.labelKey, option.fallback))))
    ),

    h("div", { className: "cjds-filterbar-group cjds-filterbar-reset" },
      h("button", { type: "button", className: "cjds-btn secondary", onClick: onReset }, t("h_cj_reset_filters", "Reset Filters"))
    )
  );
};

module.exports = { FilterBar, STATUS_OPTIONS, SORT_OPTIONS };
